import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './CompletionStates.module.css';

interface NoContentProps {
  /** Re-reads the session, so a learner who waits can pick up new content. */
  onRetry: () => void;
  /** The re-read is out. This screen stays put and the control says so. */
  retrying?: boolean;
}

/**
 * The no-available-Lesson state (T077; spec § Edge Cases).
 *
 * This looks like `AllComplete` and means the opposite. There, the learner
 * finished everything; here, there is nothing to finish — no Lesson holds an
 * Exercise yet, so `GET /learn/lessons` filters every empty one out (FR-048)
 * and the session has no position to resolve to. The server distinguishes the
 * two with `allLessonsComplete`, and the two screens exist separately because
 * telling a learner with an empty catalogue that they "completed everything"
 * would be a plain untruth.
 *
 * The requirement is a clear route to available content rather than an empty
 * screen — and the honest route, when the catalogue is genuinely empty, is
 * to say so and offer a re-check rather than to link somewhere equally empty.
 * A Lessons overview would show the same nothing; the account page is not
 * content. When content does land, this button is the whole of getting to it.
 */
export function NoContent({ onRetry, retrying = false }: NoContentProps) {
  return (
    <Surface as="section" layer={2} radius="xl" inset>
      <div className={styles.state} data-testid="no-content">
        <Badge tone="neutral" icon="alert-circle">
          Sem conteúdo
        </Badge>
        <Text as="h1" variant="heading">
          Nenhuma lição disponível ainda
        </Text>
        <Text as="p" variant="body">
          Ainda não há exercícios publicados. Sua conta está pronta — assim que a primeira lição
          for publicada, ela aparece aqui.
        </Text>
        <span data-testid="check-again">
          {retrying ? (
            <Button
              variant="secondary"
              icon="rotate-ccw"
              disabled
              disabledReason="Estamos procurando lições publicadas."
            >
              Verificando…
            </Button>
          ) : (
            <Button variant="secondary" icon="rotate-ccw" onClick={onRetry}>
              Verificar novamente
            </Button>
          )}
        </span>
      </div>
    </Surface>
  );
}
